// src/hooks/useHermes.ts

import { useState, useCallback, useEffect } from 'react';
import { create } from 'zustand';
import WorkspaceManager from '../runtime/WorkspaceManager/index';
import RuntimeManager from '../runtime/RuntimeManager';
import { RuntimeName } from '../types/runtime';

// HermesIntent 是 enum（值），用 import
import { HermesIntent } from '../types/hermes.types';

// HermesMessage, HermesResponse, HermesCommand, MessageStatus, HermesConversation 是 interface/type，用 import type
import type {
  HermesMessage,
  HermesResponse,
  HermesCommand,
  MessageStatus,
  HermesConversation,
} from '../types/hermes.types';

// WorkspaceView 是 enum（值），用 import
import { WorkspaceView } from '../types/workspace';

// ============================================
// Hermes 消息共享 Store
// 将消息状态从 useHermes 的本地 useState 提升为 zustand 全局 store，
// 使 HermesWorkspace 等组件可以读取同一份消息（提取 intent/runtime 路由信息）
// ============================================

interface HermesMessagesState {
  messages: HermesMessage[];
  setMessages: (
    updater: HermesMessage[] | ((prev: HermesMessage[]) => HermesMessage[])
  ) => void;
}

const useHermesMessagesStore = create<HermesMessagesState>((set) => ({
  messages: [],
  setMessages: (updater) =>
    set((state) => ({
      messages:
        typeof updater === 'function'
          ? (updater as (prev: HermesMessage[]) => HermesMessage[])(state.messages)
          : updater,
    })),
}));

/**
 * 读取共享的 Hermes 消息列表
 * 供 HermesWorkspace 等组件提取最新消息的 intent/runtime/view 路由信息
 */
export function useHermesMessages(): HermesMessage[] {
  return useHermesMessagesStore((s) => s.messages);
}

interface UseHermesOptions {
  /** 会话 ID（用于历史记录） */
  conversationId?: string;
  /** 是否自动加载历史 */
  autoLoadHistory?: boolean;
}

interface UseHermesReturn {
  messages: HermesMessage[];
  loading: boolean;
  error: string | null;
  conversationId: string | null;
  sendMessage: (content: string) => Promise<void>;
  sendCommand: (command: HermesCommand) => Promise<void>;
  clearMessages: () => void;
  loadHistory: (conversationId: string) => Promise<void>;
  saveHistory: () => Promise<void>;
  deleteHistory: (conversationId: string) => Promise<void>;
  appendToken: (token: string) => void;
}

/**
 * useHermes - Hermes 交互 Hook
 * 
 * 职责：
 * 1. 管理消息状态
 * 2. 调用 RuntimeManager 发送消息
 * 3. 处理流式输出（预留）
 * 4. 管理会话历史（预留）
 * 5. 通过 RuntimeManager 分发 Workspace 事件（解耦）
 */
export function useHermes(options: UseHermesOptions = {}): UseHermesReturn {
  const { conversationId: initialConversationId, autoLoadHistory = false } = options;

  // 消息状态使用全局共享 store（替代本地 useState），使 HermesWorkspace 可读取同一份消息
  const messages = useHermesMessagesStore((s) => s.messages);
  const setMessages = useHermesMessagesStore((s) => s.setMessages);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [conversationId, setConversationId] = useState<string | null>(initialConversationId || null);
  const [streamingMessageId, setStreamingMessageId] = useState<string | null>(null);

  /**
   * 生成唯一 ID
   */
  const generateId = () => `msg-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;

  /**
   * 发送消息
   */
  const sendMessage = useCallback(async (content: string) => {
    if (!content.trim()) return;

    console.log('[useHermes] sendMessage 开始:', content.trim());

    // 1. 添加用户消息
    const userMessage: HermesMessage = {
      id: generateId(),
      role: 'user',
      content: content.trim(),
      status: 'success',
      timestamp: new Date().toISOString(),
    };

    setMessages((prev) => [...prev, userMessage]);
    console.log('[useHermes] 用户消息已添加');

    // 2. 添加 AI 占位消息（loading 状态）
    const assistantId = generateId();
    const assistantPlaceholder: HermesMessage = {
      id: assistantId,
      role: 'assistant',
      content: '',
      status: 'loading',
      timestamp: new Date().toISOString(),
    };

    setMessages((prev) => [...prev, assistantPlaceholder]);
    setLoading(true);
    setError(null);
    setStreamingMessageId(assistantId);
    console.log('[useHermes] loading 占位已添加，准备调用 RuntimeManager.execute');

    try {
      // 3. 调用 RuntimeManager
      const response = await RuntimeManager.execute(
        RuntimeName.Hermes,
        'chat',
        { content: content.trim() }
      );

      console.log('[useHermes] RuntimeManager.execute 返回:', {
        success: response.success,
        hasData: !!response.data,
        error: response.error,
        latency: response.latency,
      });

      if (response.success && response.data) {
        const data = response.data as HermesResponse;

        // 4. 更新 AI 消息
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === assistantId
              ? {
                  ...msg,
                  content: data.response || '已收到您的指令',
                  status: 'success',
                  intent: data.intent,
                  runtime: data.runtime || RuntimeName.Hermes,
                  view: data.view,
                  payload: data.payload,
                  widgets: data.widgets,
                  timestamp: new Date().toISOString(),
                }
              : msg
          )
        );

        // 5. 如果返回了 view，通过 RuntimeManager 分发 Workspace 事件（解耦）
        if (data.view) {
          WorkspaceManager.switchView(data.view as WorkspaceView);
        }

        if (data.error) {
          setError(data.error);
        }
      } else {
        // 请求失败
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === assistantId
              ? {
                  ...msg,
                  content: '抱歉，我遇到了问题。请稍后重试。',
                  status: 'error',
                  error: response.error || 'Unknown error',
                  timestamp: new Date().toISOString(),
                }
              : msg
          )
        );
        setError(response.error || 'Unknown error');
      }
    } catch (err: any) {
      // 异常处理
      console.error('[useHermes] sendMessage 异常:', err);
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === assistantId
            ? {
                ...msg,
                content: '抱歉，系统出现异常。请稍后重试。',
                status: 'error',
                error: err.message || 'System error',
                timestamp: new Date().toISOString(),
              }
            : msg
        )
      );
      setError(err.message || 'System error');
    } finally {
      setLoading(false);
      setStreamingMessageId(null);
    }
  }, []);

  /**
   * 发送快捷命令
   */
  const sendCommand = useCallback(async (command: HermesCommand) => {
    await sendMessage(command.command);
  }, [sendMessage]);

  /**
   * 清空消息
   */
  const clearMessages = useCallback(() => {
    setMessages([]);
    setError(null);
  }, []);

  /**
   * 加载历史记录（预留 - Phase 6）
   */
  const loadHistory = useCallback(async (id: string) => {
    console.log('[useHermes] loadHistory:', id);
    // TODO: 从 RuntimeManager 加载历史
    setConversationId(id);
  }, []);

  /**
   * 保存历史记录（预留 - Phase 6）
   */
  const saveHistory = useCallback(async () => {
    console.log('[useHermes] saveHistory');
    // TODO: 保存到 RuntimeManager
  }, []);

  /**
   * 删除历史记录（预留 - Phase 6）
   */
  const deleteHistory = useCallback(async (id: string) => {
    console.log('[useHermes] deleteHistory:', id);
    // TODO: 从 RuntimeManager 删除
  }, []);

  /**
   * 追加 Token（流式输出 - 预留 Phase 6）
   */
  const appendToken = useCallback((token: string) => {
    if (!streamingMessageId) return;

    setMessages((prev) =>
      prev.map((msg) =>
        msg.id === streamingMessageId
          ? {
              ...msg,
              content: msg.content + token,
              status: 'streaming',
            }
          : msg
      )
    );
  }, [streamingMessageId]);

  // 自动加载历史
  useEffect(() => {
    if (autoLoadHistory && initialConversationId) {
      loadHistory(initialConversationId);
    }
  }, [autoLoadHistory, initialConversationId, loadHistory]);

  return {
    messages,
    loading,
    error,
    conversationId,
    sendMessage,
    sendCommand,
    clearMessages,
    loadHistory,
    saveHistory,
    deleteHistory,
    appendToken,
  };
}
